
import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, Tabs, Tab, Chip, useTheme, CircularProgress } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ListIcon from "@mui/icons-material/List";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessIcon from "@mui/icons-material/Business";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import Unauthorized from "./components/Unauthorized";
import usePrismTracker from "./hooks/usePrismTracker";
import { isAuthenticated, getCurrentUsername, logout } from "./api/prismAPI";
import { API_BASE_URL } from './config';
import { FeatureFlagsProvider, useFeatureFlags, Features } from './hooks/useFeatureFlags';

// Lazy load dashboard components for better performance
const ProtocolPanel = lazy(() => import("./components/ProtocolPanel"));
const GalaxyArea = lazy(() => import("./components/workflowMap/GalaxyArea"));
const ApprovedProtocols = lazy(() => import("./components/ApprovedProtocols"));
const SopLibrary = lazy(() => import("./components/SopLibrary"));
const CoachingDashboard = lazy(() => import("./components/CoachingDashboard"));
const IntelligentDashboard = lazy(() => import("./components/IntelligentDashboard"));
const CareCoordinatorDashboard = lazy(() => import("./components/CareCoordinatorDashboard"));
const AnalyticsDashboard = lazy(() => import("./components/AnalyticsDashboard"));
const CallManagementDashboard = lazy(() => import("./components/CallManagementDashboard"));
const SaaSDashboard = lazy(() => import("./components/SaaSDashboard"));
const Settings = lazy(() => import("./components/Settings"));
const DebugRole = lazy(() => import("./components/DebugRole"));

// Loading fallback component
const DashboardLoader = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
    gap: 2
  }}>
    <CircularProgress size={40} />
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
      Loading dashboard...
    </Typography>
  </Box>
);

/**
 * AppContent - The main authenticated app layout with header and navigation
 * Wrapped by BrowserRouter in the main App component
 */
const AppContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();

  const [username, setUsername] = useState("");
  const [userRole, setUserRole] = useState(null); // null = loading
  const [isValidating, setIsValidating] = useState(true);

  // Check authentication on mount AND validate token
  // Only run on mount, not on every route change
  useEffect(() => {
    const validateSession = async () => {

      // If already on login page, don't validate
      if (location.pathname === '/login') {
        setIsValidating(false);
        return;
      }

      if (!isAuthenticated()) {
        // Not authenticated, redirect to login
        navigate('/login', { replace: true });
        setIsValidating(false);
        return;
      }

      const user = getCurrentUsername();
      const token = localStorage.getItem('prism_token');

      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUsername(user);

          // Set role from response (no need for separate fetch)
          const role = data.role || 'care_coordinator';
          setUserRole(role);
          setIsValidating(false);
        } else {
          // Token expired or invalid, force re-login
          logout();
          navigate('/login', { replace: true });
          setIsValidating(false);
        }
      } catch (err) {
        logout();
        navigate('/login', { replace: true });
        setIsValidating(false);
      }
    };

    validateSession();
  }, []); // Only run on mount

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Show loading while validating session
  if (isValidating || !userRole) {
    return (
      <Box sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
          // "radial-gradient(1200px 600px at 20% -10%, rgba(67, 137, 154, 0.68) 0%, transparent 60%), radial-gradient(1200px 600px at 110% 10%, rgba(154, 83, 143, 0.6) 0%, transparent 60%), linear-gradient(rgb(11, 18, 32) 0%, rgb(52, 36, 65) 100%)",
      }}>
        <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          Setting up your workspace...
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Verifying permissions and loading data
        </Typography>
      </Box>
    );
  }

  // Get current route path for active tab highlighting
  const currentPath = location.pathname;

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(67, 137, 154, 0.68) 0%, transparent 60%), radial-gradient(1200px 600px at 110% 10%, rgba(154, 83, 143, 0.36) 0%, transparent 60%), linear-gradient(rgba(15, 24, 42, 1) 0%, rgb(52, 36, 65) 100%)",
        color: "white",
        paddingTop: "2px",
        overflow: "hidden",  // Prevent page scroll
      }}
    >
      {/* Header - Fixed - Glassmorphism */}
      <Box sx={{
        py: 2,
        px: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderBottom: "1px solid rgba(255,255,255,0.1)",
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src="/logo192.png" alt="PRISM" style={{ width: 40, height: 40 }} />
          <Tabs
            value={currentPath}
            onChange={(e, newPath) => navigate(newPath)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.14)',
                textTransform: 'none',
                fontSize: '0.95rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'white',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              }
            }}
          >
            {/* Care Coordinator & Org Clinical: Only My Performance Dashboard */}
            {(userRole === 'care_coordinator' || userRole === 'org_clinical') && [
              <Tab
                key="/dashboard"
                icon={<AssessmentIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="My Performance"
                value="/dashboard"
              />,
            ]}

            {/* Team Lead: Team Support Dashboard */}
            {userRole === 'team_lead' && [
              <Tab
                key="/coaching"
                icon={<SchoolIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Team Support"
                value="/coaching"
              />,
            ]}

            {/* Leadership: Chart Sessions + Analytics */}
            {userRole === 'leadership' && [
              <Tab
                key="/calls"
                icon={<TimerIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Chart Sessions"
                value="/calls"
              />,
              <Tab
                key="/analytics"
                icon={<TrendingUpIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Analytics"
                value="/analytics"
              />,
            ]}

            {/* Organization Admin: Organization Management + Settings */}
            {userRole === 'org_admin' && [
              <Tab
                key="/sops"
                icon={<MenuBookIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="SOP Library"
                value="/sops"
              />,
              <Tab
                key="/saas"
                icon={<BusinessIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Organization"
                value="/saas"
              />,
              <Tab
                key="/settings"
                icon={<SettingsIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Settings"
                value="/settings"
              />,
            ]}

            {/* Org/Clinical Admin: Protocols + Teams */}
            {userRole === 'platform_admin' && [
              <Tab
                key="/protocols"
                icon={<ListIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Protocol Library"
                value="/protocols"
              />,
              <Tab
                key="/approved"
                icon={<CheckCircleIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Approved Protocols"
                value="/approved"
              />,
              <Tab
                key="/sops"
                icon={<MenuBookIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="SOP Library"
                value="/sops"
              />,
              <Tab
                key="/intelligent"
                icon={<PsychologyIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="AI Insights"
                value="/intelligent"
              />,
              <Tab
                key="/coaching"
                icon={<SchoolIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Team Support"
                value="/coaching"
              />,
              <Tab
                key="/analytics"
                icon={<TrendingUpIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Analytics"
                value="/analytics"
              />,
              <Tab
                key="/calls"
                icon={<TimerIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Chart Sessions"
                value="/calls"
              />,
              <Tab
                key="/saas"
                icon={<BusinessIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Organization"
                value="/saas"
              />,
              <Tab
                key="/settings"
                icon={<SettingsIcon sx={{ fontSize: '1.1rem' }} />}
                iconPosition="start"
                label="Settings"
                value="/settings"
              />,
            ]}
          </Tabs>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon sx={{ fontSize: "1.2rem", opacity: 0.8 }} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {username}
            </Typography>
            <Chip
              label={userRole}
              size="small"
              sx={{
                bgcolor: userRole === 'admin'
                  ? theme.palette.roles.admin
                  : userRole === 'teamlead'
                    ? theme.palette.roles.teamlead
                    : theme.palette.roles.carecoordinator,
                color: 'white',
                fontWeight: 600,
                textTransform: 'capitalize',
                height: 20
              }}
            />
          </Box>
          <Button
            size="small"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ color: "white", textTransform: "none" }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main Content Area - React Router handles routing */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Suspense fallback={<DashboardLoader />}>
          <Routes>
            {/* Protocol Library - Platform Admin Only */}
            <Route
              path="/protocols"
              element={
                <ProtectedRoute allowedRoles={["platform_admin"]} userRole={userRole}>
                  <Box sx={{ 
                    flex: 1, 
                    display: "flex", 
                    flexDirection: { xs: 'column', lg: 'row' },  // Stack on mobile
                    overflow: "hidden", 
                    minHeight: 0, 
                    gap: 2, 
                    p: 2,
                    height: '100%'
                  }}>
                    {/* Left Panel - Graph (70% on desktop, 50% height on mobile) */}
                    <Box sx={{ 
                      flex: { xs: '1 1 50%', lg: '0 0 70%' },
                      display: "flex", 
                      flexDirection: "column", 
                      overflow: "hidden", 
                      minHeight: { xs: '50%', lg: 0 }
                    }}>
                      {isEnabled(Features.PROTOCOL_DISCOVERY) ? (
                        <GalaxyArea userRole={userRole} />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)' }}>Protocol Discovery - Coming Soon</Typography>
                        </Box>
                      )}
                    </Box>
                    {/* Right Panel - Protocols (30% on desktop, 50% height on mobile) */}
                    <Box sx={{ 
                      flex: { xs: '1 1 50%', lg: '0 0 30%' },
                      display: "flex", 
                      flexDirection: "column", 
                      overflow: "hidden", 
                      minHeight: { xs: '50%', lg: 0 }
                    }}>
                      <ProtocolPanel username={username} onLogout={handleLogout} />
                    </Box>
                  </Box>
                </ProtectedRoute>
              }
            />

            {/* Approved Protocols - Platform Admin Only */}
            <Route
              path="/sops"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "org_admin"]} userRole={userRole}>
                  <SopLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approved"
              element={
                <ProtectedRoute allowedRoles={["platform_admin"]} userRole={userRole}>
                  <ApprovedProtocols />
                </ProtectedRoute>
              }
            />

            {/* AI Insights Dashboard - Platform Admin Only */}
            <Route
              path="/intelligent"
              element={
                <ProtectedRoute allowedRoles={["platform_admin"]} userRole={userRole}>
                  <IntelligentDashboard />
                </ProtectedRoute>
              }
            />

            {/* Team Support Dashboard - Team Lead & Platform Admin */}
            <Route
              path="/coaching"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "team_lead"]} userRole={userRole}>
                  <CoachingDashboard />
                </ProtectedRoute>
              }
            />

            {/* Care Coordinator & Org Clinical Personal Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={["care_coordinator", "org_clinical"]} userRole={userRole}>
                  <CareCoordinatorDashboard />
                </ProtectedRoute>
              }
            />

            {/* Analytics Dashboard - Platform Admin & Leadership */}
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "leadership"]} userRole={userRole}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Organization Dashboard - Platform Admin & Org Admin */}
            <Route
              path="/saas"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "org_admin"]} userRole={userRole}>
                  <SaaSDashboard />
                </ProtectedRoute>
              }
            />

            {/* Chart Sessions Dashboard - Leadership & Platform Admin */}
            <Route
              path="/calls"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "leadership"]} userRole={userRole}>
                  <CallManagementDashboard />
                </ProtectedRoute>
              }
            />

            {/* Settings - Platform Admin & Org Admin */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={["platform_admin", "org_admin"]} userRole={userRole}>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* The old Overview page was removed; send bookmarks home */}
            <Route path="/overview" element={<Navigate to="/" replace />} />

            {/* Debug Tool - Shows Role Information */}
            <Route
              path="/debug"
              element={
                <DebugRole />
              }
            />

            {/* Unauthorized Page */}
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Default Route - Redirect to role-appropriate dashboard */}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    userRole === 'care_coordinator' || userRole === 'org_clinical' ? '/dashboard' :
                      userRole === 'team_lead' ? '/coaching' :
                        userRole === 'leadership' ? '/calls' :
                          userRole === 'platform_admin' ? '/protocols' :
                            userRole === 'org_admin' ? '/saas' :
                              '/dashboard'
                  }
                  replace
                />
              }
            />

            {/* Catch-all - Redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Box>
    </Box>
  );
};

/**
 * App - Root component with BrowserRouter
 * Handles routing between Login and authenticated app
 */
const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Login Route - Public */}
        <Route path="/login" element={<Login />} />

        {/* All Authenticated Routes */}
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
};

// Wrap with Feature Flags Provider
const AppWithFeatureFlags = () => {
  return (
    <FeatureFlagsProvider>
      <App />
    </FeatureFlagsProvider>
  );
};

export default AppWithFeatureFlags;
